/*
 * /admin (page)
 */
import {
	Badge,
	Button,
	Container,
	PageHeader,
	PageWithEffect,
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "@emr/react-ui"
import type { AuthUser } from "@emr/types"
import { getPlatformMetadata, isOnWaitlist } from "@emr/utils"
import { createFileRoute } from "@tanstack/react-router"
import { Loader2, UserCheck } from "lucide-react"
import { useMemo } from "react"
import { toast } from "sonner"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/admin")({
	component: AdminPage,
	loader: adminPageLoader,
})

function AdminPage() {
	const {
		data: users = [],
		isLoading,
		error,
		refetch,
	} = trpc.user.listUsers.useQuery()

	const approveUser = trpc.user.approveUser.useMutation({
		onSuccess: () => {
			toast.success("User approved successfully.")
			refetch()
		},
		onError: (err) => toast.error(err.message),
	})

	const waitlistUsers = useMemo(
		() => users.filter((u) => isOnWaitlist(u) && !getPlatformMetadata(u)),
		[users],
	)

	const approvedUsers = useMemo(
		() => users.filter((u) => !!getPlatformMetadata(u)),
		[users],
	)

	return (
		<PageWithEffect effect={"aurora"} centerVertical={false}>
			<Container width={"xxl"} padding={"lg"} gap={"lg"}>
				<PageHeader title={"Admin — Users"} />

				<Tabs defaultValue={"waitlist"}>
					<TabsList>
						<TabsTrigger value={"waitlist"}>
							{"Waitlist "}
							<Badge
								variant={"secondary"}
								className={"ml-1.5 px-1.5 py-0 text-xs"}
							>
								{waitlistUsers.length}
							</Badge>
						</TabsTrigger>
						<TabsTrigger value={"approved"}>
							{"Approved "}
							<Badge
								variant={"secondary"}
								className={"ml-1.5 px-1.5 py-0 text-xs"}
							>
								{approvedUsers.length}
							</Badge>
						</TabsTrigger>
						<TabsTrigger value={"all"}>
							{"All "}
							<Badge
								variant={"secondary"}
								className={"ml-1.5 px-1.5 py-0 text-xs"}
							>
								{users.length}
							</Badge>
						</TabsTrigger>
					</TabsList>

					{isLoading && (
						<div className={"flex items-center justify-center py-16"}>
							<Loader2
								className={"size-6 animate-spin text-muted-foreground"}
							/>
						</div>
					)}

					{error && (
						<div className={"py-8 text-center text-sm text-destructive"}>
							{error.message}
						</div>
					)}

					{!isLoading && !error && (
						<>
							<TabsContent value={"waitlist"}>
								<UserTable
									users={waitlistUsers}
									showApprove
									onApprove={(userId) => approveUser.mutate({ userId })}
									approvingId={
										approveUser.isPending
											? approveUser.variables?.userId
											: undefined
									}
								/>
							</TabsContent>
							<TabsContent value={"approved"}>
								<UserTable users={approvedUsers} />
							</TabsContent>
							<TabsContent value={"all"}>
								<UserTable
									users={users}
									showApprove
									onApprove={(userId) => approveUser.mutate({ userId })}
									approvingId={
										approveUser.isPending
											? approveUser.variables?.userId
											: undefined
									}
								/>
							</TabsContent>
						</>
					)}
				</Tabs>
			</Container>
		</PageWithEffect>
	)
}

type UserTableProps = {
	users: AuthUser[]
	showApprove?: boolean
	onApprove?: (userId: string) => void
	approvingId?: string
}

function UserTable({
	users,
	showApprove,
	onApprove,
	approvingId,
}: UserTableProps) {
	if (users.length === 0) {
		return (
			<p className={"py-12 text-center text-sm text-muted-foreground"}>
				No users found.
			</p>
		)
	}

	return (
		<div className={"w-full overflow-x-auto rounded-lg border mt-2"}>
			<table className={"w-full text-sm"}>
				<thead>
					<tr className={"border-b bg-muted/40 text-left"}>
						<th className={"px-4 py-3 font-medium"}>Email</th>
						<th className={"px-4 py-3 font-medium"}>Status</th>
						<th className={"px-4 py-3 font-medium"}>Date</th>
						{showApprove && (
							<th className={"px-4 py-3 font-medium"}>Actions</th>
						)}
					</tr>
				</thead>
				<tbody>
					{users.map((u) => {
						const isApproved = !!getPlatformMetadata(u)
						const isWaitlisted = !isApproved && isOnWaitlist(u)
						const joinedDate = getPlatformMetadata(u)?.joined
						const displayDate = joinedDate
							? new Date(joinedDate).toLocaleDateString()
							: new Date(u.created_at).toLocaleDateString()

						return (
							<tr
								key={u.id}
								className={"border-b last:border-0 hover:bg-muted/20"}
							>
								<td className={"px-4 py-3 text-foreground"}>{u.email}</td>
								<td className={"px-4 py-3"}>
									{isApproved ? (
										<Badge variant={"default"} className={"text-xs"}>
											Approved
										</Badge>
									) : isWaitlisted ? (
										<Badge variant={"outline"} className={"text-xs"}>
											Waitlist
										</Badge>
									) : (
										<Badge variant={"secondary"} className={"text-xs"}>
											Registered
										</Badge>
									)}
								</td>
								<td className={"px-4 py-3 text-muted-foreground"}>
									{displayDate}
								</td>
								{showApprove && (
									<td className={"px-4 py-3"}>
										{isWaitlisted && (
											<Button
												size={"sm"}
												variant={"outline"}
												disabled={approvingId === u.id}
												onClick={() => onApprove?.(u.id)}
											>
												{approvingId === u.id ? (
													<Loader2 className={"mr-1.5 size-3 animate-spin"} />
												) : (
													<UserCheck className={"mr-1.5 size-3"} />
												)}
												Approve
											</Button>
										)}
									</td>
								)}
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}
